import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    getInspectionById,
    getInspectionItems,
    updateInspectionResult,
    submitInspection,
    approveInspection,
    rejectInspection,
    uploadInspectionAttachment
} from "../../../services/api/qa/inspectionService";
import { toast } from "react-hot-toast";

function InspectionView() {
    const { id } = useParams();
    const [inspection, setInspection] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingItems, setLoadingItems] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchInspection();
        fetchItems();
    }, [id]);

    const fetchInspection = async () => {
        try {
            const res = await getInspectionById(id);
            setInspection(res.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchItems = async () => {
        try {
            setLoadingItems(true);
            const res = await getInspectionItems(id);
            setItems(res.data || []);
        } catch (error) {
            console.error(error);
            setItems([]);
        } finally {
            setLoadingItems(false);
        }
    };

    const handleUpdateItem = async (itemId, result, remarks) => {
        await updateInspectionResult(itemId, { result, remarks });

        setItems(prev =>
            prev.map(item =>
                item.id === itemId ? { ...item, result, remarks } : item
            )
        );
    };

    const handleSubmit = async () => {
        try {
            setActionLoading(true);
            await submitInspection(id);
            fetchInspection();
        } finally {
            setActionLoading(false);
        }
    };

    const handleApprove = async () => {
        await approveInspection(id);
        fetchInspection();
    };

    const handleReject = async () => {
        await rejectInspection(id);
        fetchInspection();
    };

    const handleUpload = async (file) => {
        try {
            console.log("Uploading file:", file); // debug

            await uploadInspectionAttachment(id, file);

            toast.success("File uploaded successfully");
        } catch (error) {
            console.error(error);
            toast.error(
                error?.response?.data?.message || "Upload failed"
            );
        }
    };

    const handleSaveAll = async () => {
        setActionLoading(true);
        try {
            for (let item of items) {
                await updateInspectionResult(item.id, {
                    result: item.result,
                    remarks: item.remarks,
                });
            }
            toast.success("Inspection results saved successfully");
        } catch (error) {
            console.error(error);
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <div className="p-6">Loading...</div>;
    if (!inspection) return <div className="p-6">Inspection not found</div>;

    const isDraft = inspection.status === "draft";
    const isSubmitted = inspection.status === "submitted";

    const statusColor =
        inspection.status === "approved"
            ? "bg-green-100 text-green-600"
            : inspection.status === "submitted"
                ? "bg-blue-100 text-blue-600"
                : inspection.status === "draft"
                    ? "bg-gray-100 text-gray-600"
                    : "bg-red-100 text-red-600";

    return (
        <div className="p-6 space-y-6">

            {/* HEADER */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-semibold">
                        Inspection INSP-{String(inspection.id).padStart(3, "0")}
                    </h1>
                    <p className="text-gray-500">
                        {inspection.checklist?.name}
                    </p>
                </div>

                <span className={`px-4 py-1 rounded-full text-sm font-medium capitalize ${statusColor}`}>
                    {inspection.status}
                </span>
            </div>

            {/* DETAILS CARD */}
            <div className="bg-white shadow rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4">Inspection Details</h3>

                <div className="grid md:grid-cols-3 gap-6 text-sm">
                    <Detail label="Project" value={inspection.project?.project_name} />
                    <Detail label="Vendor" value={inspection.vendor?.name || "-"} />
                    <Detail label="BOQ Item" value={inspection.boq_item?.item_name} />
                    <Detail label="Inspection Date" value={new Date(inspection.inspection_date).toLocaleDateString()} />
                    <Detail label="Inspected By" value={inspection.inspected_by || "-"} />
                </div>

                <div className="mt-6">
                    <p className="text-gray-500 text-sm">Remarks</p>
                    <p className="mt-1">{inspection.remarks || "No remarks added"}</p>
                </div>
            </div>

            {/* RESULTS CARD */}
            <div className="bg-white shadow rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4">Inspection Results</h3>

                {loadingItems ? (
                    <p>Loading items...</p>
                ) : items.length === 0 ? (
                    <p className="text-gray-500">No inspection items added</p>
                ) : (
                    <div className="overflow-x-auto">
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.id} className="border-t hover:bg-gray-50">
                                    {/* Checklist Column */}
                                    <td className="p-3 font-medium">
                                        {item.checklist_item?.check_point || "—"}
                                    </td>

                                    {/* Result Column */}
                                    <td className="p-3">
                                        <select
                                            value={(item.result || "").toLowerCase()}
                                            disabled={inspection.status !== "draft" && inspection.status !== "pending"}
                                            onChange={(e) =>
                                                setItems(prev =>
                                                    prev.map(i =>
                                                        i.id === item.id
                                                            ? { ...i, result: e.target.value }
                                                            : i
                                                    )
                                                )
                                            }
                                            className="border rounded px-3 py-1 w-full"
                                        >
                                            <option value="">Select</option>
                                            <option value="pass">Pass</option>
                                            <option value="fail">Fail</option>
                                            <option value="na">N/A</option>
                                        </select>
                                    </td>

                                    {/* Remarks Column */}
                                    <td className="p-3">
                                        <input
                                            type="text"
                                            value={item.remarks || ""}
                                            disabled={inspection.status !== "draft" && inspection.status !== "pending"}
                                            onChange={(e) =>
                                                setItems(prev =>
                                                    prev.map(i =>
                                                        i.id === item.id
                                                            ? { ...i, remarks: e.target.value }
                                                            : i
                                                    )
                                                )
                                            }
                                            className="border rounded px-3 py-1 w-full"
                                            placeholder="Enter remarks"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {isDraft && (
                            <button
                                onClick={handleSaveAll}
                                className="px-5 py-2 bg-indigo-600 text-white rounded"
                            >
                                Save Changes
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ACTION SECTION */}
            <div className="bg-white shadow rounded-xl p-6 space-y-4">

                <div className="flex flex-wrap gap-4">
                    {isDraft && (
                        <button
                            onClick={handleSubmit}
                            disabled={actionLoading}
                            className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            {actionLoading ? "Submitting..." : "Submit Inspection"}
                        </button>
                    )}

                    {isSubmitted && (
                        <>
                            <button
                                onClick={handleApprove}
                                className="px-5 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                            >
                                Approve
                            </button>
                            <button
                                onClick={handleReject}
                                className="px-5 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Reject
                            </button>
                        </>
                    )}
                </div>

                {isDraft && (
                    <div>
                        <p className="text-sm text-gray-500 mb-2">Upload Attachment</p>
                        <input
                            type="file"
                            onChange={(e) => {
                                const selectedFile = e.target.files[0];

                                if (!selectedFile) {
                                    return;
                                }

                                handleUpload(selectedFile);
                            }}
                        />
                    </div>
                )}
            </div>

            {/* BACK BUTTON */}
            <div>
                <Link
                    to="/inspections"
                    className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                    Back to List
                </Link>
            </div>
        </div>
    );
}

function Detail({ label, value }) {
    return (
        <div>
            <p className="text-gray-500">{label}</p>
            <p className="font-medium">{value || "-"}</p>
        </div>
    );
}

export default InspectionView;